/**
 * Policy validation utility functions
 * Provides common validation logic that can be shared across different policy types
 */

import { getTodayFormatted } from './dateUtils';

/**
 * Validates that at least one change option is selected
 * 
 * @param {Object} selectedOptions - Object where keys are option names and values are booleans
 * @returns {string} Error message if invalid, empty string if valid
 */
export function validateChangeSelection(selectedOptions) {
  if (!Object.values(selectedOptions).some(isSelected => isSelected)) {
    return 'Please select at least one change option';
  }
  return '';
}

/**
 * Validates a date field is not empty and is in the future
 * 
 * @param {string} date - Date string to validate
 * @returns {string} Error message if invalid, empty string if valid
 */
export function validateEffectiveDate(date) {
  if (!date) {
    return 'Effective date is required';
  }
  
  // Compare dates in YYYY-MM-DD format
  const selectedDate = date.split('T')[0]; // Ensure we're only comparing the date part
  const today = getTodayFormatted();
  
  if (selectedDate < today) {
    return 'Effective date must be today or in the future';
  }
  
  return '';
}

/**
 * Validates if a coverage amount falls within valid range
 * 
 * @param {number} amount - Amount to validate
 * @param {number} min - Minimum valid amount
 * @param {number} max - Maximum valid amount
 * @returns {string} Error message if invalid, empty string if valid
 */
export function validateCoverageAmount(amount, min, max) {
  if (!amount && amount !== 0) {
    return 'Coverage amount is required';
  }
  
  if (isNaN(amount)) {
    return 'Coverage amount must be a number';
  }
  
  if (amount < min || amount > max) {
    return `Coverage amount must be between $${min.toLocaleString()} and $${max.toLocaleString()}`;
  }
  
  return '';
}

/**
 * Validates if a deductible amount is valid
 * 
 * @param {number} amount - Deductible amount to validate
 * @param {Array} validOptions - Array of valid deductible options
 * @returns {string} Error message if invalid, empty string if valid
 */
export function validateDeductible(amount, validOptions) {
  if (!amount && amount !== 0) {
    return 'Deductible amount is required';
  }
  
  if (isNaN(amount)) {
    return 'Deductible amount must be a number';
  }
  
  if (!validOptions.includes(Number(amount))) {
    return `Deductible must be one of the following values: ${validOptions.map(v => '$' + v.toLocaleString()).join(', ')}`;
  }
  
  return '';
} 